/**
 * Control Panel
 *
 * Per-user controller status and emergency controls.
 * Uses the centralized api.ts client — no direct fetch() calls.
 */

import React, { useState } from 'react';
import { RefreshCw, Building, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { useTitanStore } from '../../stores/titanStore';
import { titanApi, ApiError } from '../../lib/api';
import type { TitanStatus } from '../../types/titan';

interface StatusIndicatorProps {
  label: string;
  connected: boolean;
  tooltip?: string;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ label, connected, tooltip }) => (
  <div title={tooltip || ''}>
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
    </div>
  </div>
);

const ControlPanel: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { systemStatus, fetchSystemStatus } = useTitanStore();

  const call = async (action: string, fn: () => Promise<unknown>) => {
    setLoading(action);
    setError(null);
    try {
      await fn();
      await fetchSystemStatus();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : `Failed to ${action}`);
    } finally {
      setLoading(null);
    }
  };

  const isRunning = systemStatus?.status === 'running';
  const isPaused  = systemStatus?.status === 'paused';

  const statusColor = isRunning
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : isPaused
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
      : 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400';

  const rawCb = (systemStatus as any)?.circuit_breaker;
  const circuitBreaker =
    rawCb &&
    typeof rawCb === 'object' &&
    typeof (rawCb as { status?: string }).status === 'string' &&
    (rawCb as { status: string }).status.length > 0
      ? rawCb
      : null;

  return (
    <div className="rounded-lg border bg-card p-3 md:p-4 space-y-3 md:space-y-4">
      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4 justify-between">
        {/* Status & Mode */}
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-semibold capitalize ${statusColor}`}>
                {systemStatus?.status || 'stopped'}
              </span>
            </div>
            <div title={(systemStatus as any)?.mode === 'full' ? 'Broker connected' : 'Scan only — no trades'}>
              <div className={`inline-flex items-center gap-1.5 w-max font-semibold px-2.5 py-1 rounded-full text-sm ${(systemStatus as any)?.mode === 'full' ? 'bg-amber-500/10 text-amber-700' : 'bg-blue-500/10 text-blue-700'}`}>
                {(systemStatus as any)?.mode === 'full' && <Building className="w-4 h-4" />}
                <span>{(systemStatus as any)?.mode === 'full' ? 'Live Trading' : 'Scan Only Mode'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-center px-3 py-1.5 rounded-lg bg-muted/50">
              <span className="text-lg font-bold block">{(systemStatus as any)?.active_patterns || 0}</span>
              <span className="text-xs text-muted-foreground">Patterns</span>
            </div>
            <div className="text-center px-3 py-1.5 rounded-lg bg-muted/50">
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400 block">
                {(systemStatus as TitanStatus)?.broker_open_positions
                  ?? (systemStatus as TitanStatus)?.open_positions
                  ?? 0}
              </span>
              <span className="text-xs text-muted-foreground">Broker positions</span>
              {(systemStatus as TitanStatus)?.titan_open_positions != null
                && (systemStatus as TitanStatus).titan_open_positions
                  !== ((systemStatus as TitanStatus)?.broker_open_positions
                    ?? (systemStatus as TitanStatus)?.open_positions) && (
                <span className="text-[10px] text-muted-foreground block">
                  Titan: {(systemStatus as TitanStatus).titan_open_positions}
                </span>
              )}
            </div>
            <StatusIndicator label="EODHD"  connected={(systemStatus as any)?.eodhd_connected  || false} tooltip="Market data connection" />
            <StatusIndicator label="Broker" connected={(systemStatus as any)?.broker_connected || false} tooltip="Broker connection" />
            {(systemStatus as any)?.regime && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-purple-500/10 text-purple-700 dark:text-purple-400">
                {((systemStatus as any).regime.regime || 'UNKNOWN').replace('_', ' ').toUpperCase()}
              </span>
            )}
            {circuitBreaker && circuitBreaker.status !== 'normal' && (
              <div className="inline-flex items-center gap-1.5 w-max font-semibold px-2.5 py-1 rounded-full text-sm bg-red-500/10 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span>Circuit Breaker: {circuitBreaker.status}</span>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {isPaused ? (
            <Button
              onClick={() => call('resume-trading', titanApi.resetKillSwitch)}
              disabled={loading !== null}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Resume Trading
            </Button>
          ) : (
            <Button
              onClick={() => call('emergency-halt', () => titanApi.killSwitch(false))}
              disabled={loading !== null}
              variant="outline"
              className="border-rose-500 text-rose-600 hover:bg-rose-500/10"
            >
              <ShieldAlert className="w-4 h-4 mr-1" />
              Emergency Halt
            </Button>
          )}
          {isRunning && (
            <div title="Reload bots from storage">
              <Button variant="ghost" size="icon" onClick={() => call('reload', titanApi.reloadPatterns)} disabled={loading !== null}>
                {loading === 'reload' ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between p-4 rounded bg-red-500/10 text-red-700 border border-red-500/20">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="opacity-70 hover:opacity-100">&times;</button>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        Controller lifecycle is managed per user. Use <strong>Emergency Halt</strong> to pause only your trading activity.
      </div>

      {circuitBreaker && circuitBreaker.status !== 'normal' && (
        <div className="flex gap-3 p-4 rounded bg-orange-500/10 text-orange-800 border border-orange-500/20">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Circuit Breaker: {circuitBreaker.status?.toUpperCase() || 'UNKNOWN'}</span>
            <span className="text-sm opacity-90">
              {circuitBreaker.trip_reason && `Reason: ${circuitBreaker.trip_reason}`}
              {circuitBreaker.daily_pnl !== undefined && ` | Daily P&L: $${circuitBreaker.daily_pnl.toFixed(2)}`}
              {circuitBreaker.consecutive_losses !== undefined && ` | Consecutive Losses: ${circuitBreaker.consecutive_losses}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;
